import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { importOrcamento, ProgressoImportacao, confirmacaoPedida } from '../../../api/orcamentoCiliaApi';
import { ConfirmacaoVinculo } from '../../../interfaces/OrcamentoCilia';
import { ORCAMENTOS_CILIA_DETAIL } from '../routes';
import './ImportXmlModal.css';
import '../../../shared/feedback/aviso-de-tela.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface ImportXmlModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Modo "Subir complemento" (botão da tela de detalhe, 29/07/2026): só aceita
  // XML complementar DESTE orçamento/veículo — qualquer outro arquivo é
  // bloqueado na seleção. Sem a prop, comporta-se como o import geral da
  // listagem (aceita iniciais e complementos de qualquer veículo).
  restricaoComplemento?: { numeroCilia: string; placa: string };
}

// Campos mínimos para a checagem local do modo restrito (a validação completa
// continua no backend — isto é só feedback imediato na seleção do arquivo).
const lerCamposXml = (texto: string) => {
  const pega = (tag: string) => {
    const m = texto.match(new RegExp(`<${tag}>\\s*([^<]*?)\\s*<\\/${tag}>`));
    return m ? m[1].trim() : '';
  };
  return {
    numeroOrcamento: pega('numero_orcamento'),
    versao: parseInt(pega('versao_orcamento'), 10) || 1,
    placa: (texto.match(/<placa>\s*([^<]*?)\s*<\/placa>/)?.[1] ?? '').toUpperCase().replace(/[^A-Z0-9]/g, ''),
  };
};

const ImportXmlModal: React.FC<ImportXmlModalProps> = ({ isOpen, onClose, restricaoComplemento }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progresso, setProgresso] = useState<ProgressoImportacao | null>(null);
  // at-12: a importação pediu confirmação (placa sem atendimento na Oficina, ou com outro
  // evento). A pergunta é feita DENTRO deste modal — um modal filho por cima seria fechado pelo
  // clique fora do hospedeiro.
  const [confirmacao, setConfirmacao] = useState<{ tipo: ConfirmacaoVinculo; mensagem: string } | null>(null);
  // at-17: requisição de teste. Começa desmarcado a cada abertura: marcar por engano numa
  // requisição real faria o comprador não comprar a peça.
  const [teste, setTeste] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setTeste(false);
      setSelectedFile(null);
      setIsLoading(false);
      setProgresso(null);
      setConfirmacao(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const validateAndSetFile = async (file: File) => {
    if (file.type !== "text/xml" && !file.name.endsWith(".xml")) {
      toast.error("Por favor, selecione apenas arquivos XML.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("O tamanho do arquivo não pode exceder 5MB.");
      return;
    }

    // Modo restrito: só complemento do próprio orçamento/veículo desta tela.
    if (restricaoComplemento) {
      const campos = lerCamposXml(await file.text());
      if (campos.numeroOrcamento && campos.numeroOrcamento !== restricaoComplemento.numeroCilia) {
        toast.error(
          `Este XML é do orçamento ${campos.numeroOrcamento} — aqui só entram complementos do orçamento ${restricaoComplemento.numeroCilia} (${restricaoComplemento.placa}). Para importar outro orçamento, use o "+ Importar XML" da listagem.`,
        );
        return;
      }
      if (campos.placa && campos.placa !== restricaoComplemento.placa) {
        toast.error(
          `Este XML é do veículo ${campos.placa} — aqui só entram complementos do ${restricaoComplemento.placa}.`,
        );
        return;
      }
      if (campos.versao <= 1) {
        toast.error(
          'Este XML é um orçamento inicial (versão 1), não um complemento. O inicial deste veículo já está importado.',
        );
        return;
      }
    }

    setSelectedFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  // A consulta ao Nasajon (peça a peça) é a etapa longa — fica com a maior
  // fatia da barra; as pontas são rápidas e ganham só um empurrão visual.
  const percentual = (() => {
    if (!progresso) return 0;
    if (progresso.fase === 'vinculado') return 8;
    if (progresso.fase === 'salvando') return 96;
    const { atual, total } = progresso;
    if (!total) return 90;
    return Math.min(95, 10 + Math.round((atual / total) * 85));
  })();

  const rotuloProgresso = (() => {
    if (!progresso) return 'Enviando o arquivo...';
    if (progresso.fase === 'vinculado') {
      return `Vinculado à placa ${progresso.placa} · ${progresso.totalItens} itens no orçamento`;
    }
    if (progresso.fase === 'salvando') return 'Salvando o orçamento...';
    return progresso.total
      ? `Consultando o Nasajon... ${progresso.atual} de ${progresso.total} peças`
      : 'Consultando o Nasajon...';
  })();

  const handleUpload = async (confirmado?: ConfirmacaoVinculo) => {
    if (!selectedFile) return;
    setIsLoading(true);
    setProgresso(null);
    setConfirmacao(null);

    try {
      const response = await importOrcamento(selectedFile, setProgresso, confirmado, teste);
      toast.success("Orçamento importado com sucesso!");

      (response.avisos ?? []).forEach((aviso: string) => {
        toast.warn(aviso, { autoClose: 12000 });
      });

      onClose();
      navigate(ORCAMENTOS_CILIA_DETAIL(response.orcamento.id));
    } catch (err: any) {
      // Pergunta, não recusa: a mensagem do servidor diz o que acontece se a pessoa seguir.
      const tipo = confirmacaoPedida(err);
      if (tipo && !confirmado) {
        setConfirmacao({ tipo, mensagem: err.message });
        return;
      }
      toast.error(err.message || "Ocorreu um erro ao importar o arquivo XML.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="import-xml-overlay" onClick={isLoading ? undefined : onClose}>
      <div className="import-xml-box" onClick={(e) => e.stopPropagation()}>
        <div className="import-xml-header">
          <h3>
            {restricaoComplemento
              ? `Subir complemento — orçamento ${restricaoComplemento.numeroCilia} (${restricaoComplemento.placa})`
              : 'Importar XML de orçamento do Cilia'}
          </h3>
          {!isLoading && (
            <button className="close-button" onClick={onClose}>
              &times;
            </button>
          )}
        </div>

        <div className="import-xml-body">
          {confirmacao && !isLoading ? (
            <div className="aviso-de-tela" role="alert">
              <span className="aviso-de-tela-titulo">
                {confirmacao.tipo === 'EVENTO_DIVERGENTE'
                  ? 'Atenção: esta placa já está na Oficina com outro evento'
                  : 'Esta placa não tem atendimento aberto na Oficina'}
              </span>
              {confirmacao.mensagem}
              {confirmacao.tipo === 'EVENTO_DIVERGENTE'
                ? ' Tenha certeza de que este orçamento não é daquele atendimento antes de continuar.'
                : ''}
            </div>
          ) : isLoading ? (
            <div className="import-progress">
              <div className="import-progress-arquivo">
                <span className="import-xml-file-name">{selectedFile?.name}</span>
              </div>
              <div className="import-progress-track">
                <div className="import-progress-fill" style={{ width: `${percentual}%` }} />
              </div>
              <div className="import-progress-caption">
                <strong>{percentual}%</strong>
                <span>{rotuloProgresso}</span>
              </div>
            </div>
          ) : (
          <div
            className={`import-xml-dropzone ${isDragActive ? 'active' : ''} ${selectedFile ? 'has-file' : ''}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={isLoading ? undefined : triggerFileSelect}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xml"
              style={{ display: 'none' }}
              disabled={isLoading}
            />

            {selectedFile ? (
              <div className="import-xml-file-info">
                <span className="import-xml-file-name">{selectedFile.name}</span>
                <span className="import-xml-file-size">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </span>
              </div>
            ) : (
              <div className="import-xml-dropzone-prompt">
                <p>Arraste e solte o arquivo XML aqui ou clique para selecionar.</p>
                <span>Apenas arquivos .xml de até 5MB</span>
              </div>
            )}
          </div>
          )}
          {!isLoading && !confirmacao && (
            <>
              <label className="import-xml-teste">
                <input type="checkbox" checked={teste} onChange={(e) => setTeste(e.target.checked)} />
                Requisição de teste
              </label>
              <p className="import-xml-teste-hint">
                {teste
                  ? 'A requisição será criada no Nasajon com o Motivo começando por "TESTE - NÃO COMPRAR". É assim que a Nasajon identifica uma requisição de teste, e o comprador não compra as peças.'
                  : 'Marque só para testar o envio ao Nasajon. Requisição de teste não deve ser comprada.'}
              </p>
            </>
          )}
        </div>

        <div className="import-xml-footer">
          <button
            className="import-xml-btn cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </button>
          {confirmacao && !isLoading ? (
            <button className="import-xml-btn confirm" onClick={() => handleUpload(confirmacao.tipo)}>
              Iniciar a requisição mesmo assim
            </button>
          ) : (
            <button
              className="import-xml-btn confirm"
              onClick={() => handleUpload()}
              disabled={!selectedFile || isLoading}
            >
              {isLoading ? 'Importando...' : 'Importar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportXmlModal;
